import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TimePickerField } from "@/components/TimePicker";
import {
  ChevronDown, Phone, MessageCircle, Mail, MapPin, ExternalLink,
  Users, Globe, Heart, Receipt, FileText, AlertCircle, CheckCircle2,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// WeddingMobileAccordion (Feb 2026 C1 sprint)
//
// Mobile-first replacement of the linear Wedding tab form. Renders the
// EXACT same fields with the EXACT same callbacks as the desktop layout
// — only the container changes from a long scroll to a stack of
// collapsible cards. This is rendered inside an `md:hidden` wrapper in
// ClientDetail.jsx, so desktop is untouched.
//
// Cards (in order):
//   1. CONTATTI            — open by default. Adds inline Call/WhatsApp
//                            deep links right under the phone field.
//   2. EVENTO              — open by default. Date, persone, 3 orari.
//   3. LOCATION            — closed by default. Already had "Apri Maps"
//                            buttons in desktop; we keep them, full-width
//                            on mobile.
//   4. LINGUA & NAZIONALITÀ — closed by default.
//   5. BUDGET & STATO      — closed by default.
//   6. NOTE EVENTO         — closed by default.
//   7. FATTURAZIONE        — closed by default. Wraps existing BillingForm.
//   8. NOTE INTERNE        — closed by default.
//
// All field components used here (Field, TimePickerField, BillingForm,
// Select, Input, Textarea) are received via props or re-imported, NOT
// re-implemented. Zero new business logic.
// ─────────────────────────────────────────────────────────────────────────────

// Light tel: / wa.me helpers — kept inline to avoid creating a shared
// `lib/deepLinks.js` in this sprint (Wedding C1 scope only). Will be
// extracted in a follow-up cleanup sprint once Overview and other tabs
// adopt the same helpers.
function _cleanPhone(raw) {
  return raw ? String(raw).replace(/[^\d+]/g, "") : "";
}
function _telHref(raw) {
  const p = _cleanPhone(raw);
  return p ? `tel:${p}` : "";
}
function _whatsappHref(raw, msg) {
  const p = _cleanPhone(raw).replace(/^\+/, "");
  if (!p) return "";
  return msg ? `https://wa.me/${p}?text=${encodeURIComponent(msg)}` : `https://wa.me/${p}`;
}

// Accordion card primitive. Keeps the open/closed state local — the
// parent doesn't need to know which cards are open. The chevron rotates
// 180° when expanded to give a clear affordance.
function AccCard({ id, icon: Icon, title, badge, defaultOpen = false, children, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className="border border-border rounded-sm bg-white overflow-hidden"
      data-testid={testid || `wedding-acc-${id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 active:bg-neutral-50 transition-colors"
        aria-expanded={open}
        data-testid={`wedding-acc-${id}-toggle`}
      >
        {Icon && (
          <Icon className="w-4 h-4 text-primary shrink-0" strokeWidth={1.5} aria-hidden="true" />
        )}
        <span className="font-serif-heading text-base text-neutral-900 flex-1 text-left">
          {title}
        </span>
        {badge}
        <ChevronDown
          className={`w-4 h-4 text-neutral-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={1.5}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-4 border-t border-border" data-testid={`wedding-acc-${id}-body`}>
          {children}
        </div>
      )}
    </div>
  );
}

// Tap-to-act CTA used under the phone field. Mirrors the Events mobile
// pattern but rendered inline at full-width so it's reachable with a
// single thumb without competing with the floating action bar.
function InlineContactCTA({ phone, email, clientName }) {
  const tel = _telHref(phone);
  const wa = _whatsappHref(
    phone,
    clientName ? `Ciao ${clientName}, ti scrivo per il tuo matrimonio.` : "",
  );
  const mailto = email ? `mailto:${email}` : "";

  return (
    <div className="grid grid-cols-3 gap-2" data-testid="wedding-contacts-actions">
      <a
        href={tel || undefined}
        onClick={(e) => { if (!tel) e.preventDefault(); }}
        aria-disabled={!tel}
        className={`flex flex-col items-center justify-center gap-0.5 h-12 rounded-md border transition-colors ${
          tel
            ? "bg-emerald-50 border-emerald-200 text-emerald-800 active:bg-emerald-100"
            : "bg-neutral-50 border-neutral-200 text-neutral-300"
        }`}
        data-testid="wedding-contacts-call"
      >
        <Phone className="w-4 h-4" strokeWidth={1.7} />
        <span className="text-[11px] font-medium uppercase tracking-wide">Chiama</span>
      </a>
      <a
        href={wa || undefined}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => { if (!wa) e.preventDefault(); }}
        aria-disabled={!wa}
        className={`flex flex-col items-center justify-center gap-0.5 h-12 rounded-md border transition-colors ${
          wa
            ? "bg-green-50 border-green-200 text-green-800 active:bg-green-100"
            : "bg-neutral-50 border-neutral-200 text-neutral-300"
        }`}
        data-testid="wedding-contacts-whatsapp"
      >
        <MessageCircle className="w-4 h-4" strokeWidth={1.7} />
        <span className="text-[11px] font-medium uppercase tracking-wide">WhatsApp</span>
      </a>
      <a
        href={mailto || undefined}
        onClick={(e) => { if (!mailto) e.preventDefault(); }}
        aria-disabled={!mailto}
        className={`flex flex-col items-center justify-center gap-0.5 h-12 rounded-md border transition-colors ${
          mailto
            ? "bg-blue-50 border-blue-200 text-blue-800 active:bg-blue-100"
            : "bg-neutral-50 border-neutral-200 text-neutral-300"
        }`}
        data-testid="wedding-contacts-email"
      >
        <Mail className="w-4 h-4" strokeWidth={1.7} />
        <span className="text-[11px] font-medium uppercase tracking-wide">Email</span>
      </a>
    </div>
  );
}

// Lightweight Field wrapper for mobile — reuses Input + Label styling but
// stacks vertically with comfortable spacing for touch input. Kept local
// to this file so changes to the desktop `Field` helper inside
// ClientDetail.jsx never accidentally affect mobile and vice versa.
function MField({ label, value, onChange, type = "text", placeholder, testid, hint }) {
  return (
    <div>
      <Label className="label-uppercase">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-1 rounded-sm"
      />
      {hint && <p className="text-[11px] text-neutral-500 mt-1">{hint}</p>}
    </div>
  );
}

export default function WeddingMobileAccordion({
  client,
  patch,
  setClient,
  // Constants from the parent (keeps the source of truth in one place)
  NATIONALITIES,
  LANGUAGES,
  CLIENT_STATUSES,
  // Pre-computed maps URLs (already memoized in parent)
  prepMapsUrl,
  mapsUrl,
  // Billing
  BillingForm,
  validateBilling,
  // Toast for nationality → language hint (parent passes its existing toast import)
  onSuggestLanguageToast,
}) {
  // 1. Resolve billing badge once per render (same logic as desktop)
  let billingBadge;
  if (!client.billing || !client.billing.type) {
    billingBadge = (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-neutral-50 text-neutral-500 border border-neutral-200"
        data-testid="wedding-acc-billing-empty"
      >
        Da compilare
      </span>
    );
  } else {
    const err = validateBilling(client.billing);
    billingBadge = err ? (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-neutral-50 text-neutral-600 border border-neutral-200"
        data-testid="wedding-acc-billing-incomplete"
      >
        <AlertCircle className="w-3 h-3" strokeWidth={1.5} /> Incompleto
      </span>
    ) : (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200"
        data-testid="wedding-acc-billing-complete"
      >
        <CheckCircle2 className="w-3 h-3" strokeWidth={1.5} /> Completi
      </span>
    );
  }

  return (
    <div className="space-y-3" data-testid="wedding-mobile-accordion">
      {/* 1. CONTATTI — open by default */}
      <AccCard id="contacts" icon={Users} title="Contatti" defaultOpen>
        <MField
          label="Nome sposa"
          value={client.name}
          onChange={(v) => patch("name", v)}
          testid="wedding-acc-name"
        />
        <MField
          label="Partner / Wedding planner"
          value={client.partner_name || ""}
          onChange={(v) => patch("partner_name", v)}
          testid="wedding-acc-partner"
        />
        <MField
          label="Email"
          type="email"
          value={client.email || ""}
          onChange={(v) => patch("email", v)}
          testid="wedding-acc-email"
        />
        <MField
          label="Telefono"
          type="tel"
          value={client.phone || ""}
          onChange={(v) => patch("phone", v)}
          testid="wedding-acc-phone"
        />
        {/* Inline tap-to-act CTAs — the heart of mobile-first UX */}
        <InlineContactCTA phone={client.phone} email={client.email} clientName={client.name} />
      </AccCard>

      {/* 2. EVENTO — open by default */}
      <AccCard id="event" icon={Heart} title="Evento" defaultOpen>
        <MField
          label="Data matrimonio"
          type="date"
          value={client.wedding_date || ""}
          onChange={(v) => patch("wedding_date", v)}
          testid="wedding-acc-date"
        />
        <MField
          label="Persone da preparare"
          type="number"
          value={client.people_count ?? 1}
          onChange={(v) => patch("people_count", parseInt(v, 10) || 0)}
          testid="wedding-acc-people"
        />
        <TimePickerField
          label="Orario cerimonia"
          value={client.ceremony_time || ""}
          onChange={(v) => patch("ceremony_time", v)}
          testid="wedding-acc-ceremony-time"
        />
        <TimePickerField
          label="Orario uscita sposa"
          value={client.bride_exit_time || ""}
          onChange={(v) => patch("bride_exit_time", v)}
          testid="wedding-acc-bride-exit-time"
        />
        <TimePickerField
          label="Orario inizio lavoro"
          value={client.work_start_time || ""}
          onChange={(v) => patch("work_start_time", v)}
          testid="wedding-acc-work-start"
        />
      </AccCard>

      {/* 3. LOCATION — open by default for daily-driver use case (operator
          needs to verify the venue on Maps quickly) */}
      <AccCard id="location" icon={MapPin} title="Location" defaultOpen>
        <div>
          <Label className="label-uppercase">Luogo preparazione</Label>
          <Input
            value={client.prep_venue || ""}
            onChange={(e) => patch("prep_venue", e.target.value)}
            placeholder="Es. Casa della sposa, Hotel Continental"
            className="mt-1 rounded-sm"
            data-testid="wedding-acc-prep-venue"
          />
          <a
            href={prepMapsUrl || undefined}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { if (!prepMapsUrl) e.preventDefault(); }}
            aria-disabled={!prepMapsUrl}
            className={`mt-2 flex items-center justify-center gap-1.5 h-11 rounded-md border w-full transition-colors ${
              prepMapsUrl
                ? "bg-blue-50 border-blue-200 text-blue-800 active:bg-blue-100"
                : "bg-neutral-50 border-neutral-200 text-neutral-300"
            }`}
            data-testid="wedding-acc-prep-maps"
          >
            <ExternalLink className="w-4 h-4" strokeWidth={1.7} />
            <span className="text-[12.5px] font-medium">Apri preparazione in Maps</span>
          </a>
          <p className="text-[11px] text-neutral-500 mt-2">
            Dove ti rechi per il make-up/hair (spesso diverso dalla cerimonia).
          </p>
        </div>
        <div>
          <Label className="label-uppercase">Location cerimonia</Label>
          <Input
            value={client.venue || ""}
            onChange={(e) => patch("venue", e.target.value)}
            placeholder="Es. Villa dei Cedri, Via Roma 12, Firenze"
            className="mt-1 rounded-sm"
            data-testid="wedding-acc-venue"
          />
          <a
            href={mapsUrl || undefined}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { if (!mapsUrl) e.preventDefault(); }}
            aria-disabled={!mapsUrl}
            className={`mt-2 flex items-center justify-center gap-1.5 h-11 rounded-md border w-full transition-colors ${
              mapsUrl
                ? "bg-blue-50 border-blue-200 text-blue-800 active:bg-blue-100"
                : "bg-neutral-50 border-neutral-200 text-neutral-300"
            }`}
            data-testid="wedding-acc-venue-maps"
          >
            <ExternalLink className="w-4 h-4" strokeWidth={1.7} />
            <span className="text-[12.5px] font-medium">Apri cerimonia in Maps</span>
          </a>
        </div>
      </AccCard>

      {/* 4. LINGUA & NAZIONALITÀ — closed by default */}
      <AccCard id="language" icon={Globe} title="Lingua e nazionalità">
        <p className="text-[11px] text-neutral-500 -mt-1">
          La lingua selezionata sarà usata per generare preventivo, contratto ed email.
        </p>
        <div>
          <Label className="label-uppercase">Nazionalità</Label>
          <Select
            value={client.nationality || ""}
            onValueChange={(v) => {
              const nat = NATIONALITIES.find((n) => n.code === v);
              const next = { ...client, nationality: v };
              if (nat?.suggest && (!client.language || client.language === "it") && nat.suggest !== "it") {
                next.language = nat.suggest;
                if (typeof onSuggestLanguageToast === "function") {
                  const langLabel = LANGUAGES.find((l) => l.v === nat.suggest)?.label || nat.suggest;
                  onSuggestLanguageToast(langLabel);
                }
              }
              setClient(next);
            }}
          >
            <SelectTrigger className="mt-1 rounded-sm" data-testid="wedding-acc-nationality">
              <SelectValue placeholder="Seleziona nazionalità" />
            </SelectTrigger>
            <SelectContent>
              {NATIONALITIES.map((n) => (
                <SelectItem key={n.code} value={n.code}>{n.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-uppercase">Lingua documenti</Label>
          <Select value={client.language || "it"} onValueChange={(v) => patch("language", v)}>
            <SelectTrigger className="mt-1 rounded-sm" data-testid="wedding-acc-language">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGES.map((l) => (
                <SelectItem key={l.v} value={l.v}>{l.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </AccCard>

      {/* 5. BUDGET & STATO — closed by default */}
      <AccCard id="budget" icon={Receipt} title="Budget e stato">
        <MField
          label="Budget (€)"
          type="number"
          value={client.budget || 0}
          onChange={(v) => patch("budget", v)}
          testid="wedding-acc-budget"
        />
        <div>
          <Label className="label-uppercase">Stato cliente</Label>
          <Select value={client.status} onValueChange={(v) => patch("status", v)}>
            <SelectTrigger className="mt-1 rounded-sm" data-testid="wedding-acc-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CLIENT_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </AccCard>

      {/* 6. NOTE EVENTO — closed by default */}
      <AccCard id="event-notes" icon={FileText} title="Note evento">
        <Textarea
          rows={5}
          value={client.event_notes || ""}
          onChange={(e) => patch("event_notes", e.target.value)}
          placeholder="Indicazioni operative per il giorno: ordine di preparazione, contatti del coordinatore, dress-code, ecc."
          className="mt-1 rounded-sm"
          data-testid="wedding-acc-event-notes"
        />
      </AccCard>

      {/* 7. FATTURAZIONE — closed by default. Wraps existing BillingForm
          (no logic change), just gives it a clear collapsible boundary. */}
      <AccCard id="billing" icon={Receipt} title="Fatturazione cliente" badge={billingBadge}>
        <p className="text-[11px] text-neutral-500 -mt-1">
          <strong className="text-neutral-700">Facoltativi.</strong>{" "}
          Servono per la fattura finale. Preventivo, contratto e firma elettronica
          si inviano regolarmente anche senza questi dati.
        </p>
        <BillingForm
          value={client.billing}
          onChange={(v) => patch("billing", v)}
          scope="client"
        />
        {client.billing?.type && (() => {
          const err = validateBilling(client.billing);
          return err ? (
            <p className="text-[11px] text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-sm p-2.5">
              {err}.{" "}
              <span className="text-neutral-500">
                Puoi compilare i dati anche più avanti — non bloccano l&apos;invio del preventivo né la firma del contratto.
              </span>
            </p>
          ) : null;
        })()}
      </AccCard>

      {/* 8. NOTE INTERNE — closed by default */}
      <AccCard id="notes" icon={FileText} title="Note interne (CRM)">
        <Textarea
          rows={3}
          value={client.notes || ""}
          onChange={(e) => patch("notes", e.target.value)}
          placeholder="Promemoria interni, follow-up, preferenze generali..."
          className="mt-1 rounded-sm"
          data-testid="wedding-acc-notes"
        />
      </AccCard>
    </div>
  );
}
